import { cn } from '@/lib/cn';
import { dataColors } from './design-tokens';

/** Deterministic colour so the same person is always the same swatch. */
function toneFor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return dataColors.avatars[hash % dataColors.avatars.length]!;
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => [...p][0] ?? '').join('').toUpperCase();
}

export function Avatar({ name, src, size = 32, className }: {
  name: string; src?: string | null; size?: number; className?: string;
}) {
  if (src) {
    return (
      <img
        src={src} alt={name} width={size} height={size}
        className={cn('rounded-full object-cover', className)}
      />
    );
  }
  return (
    <span
      aria-label={name}
      title={name}
      style={{ width: size, height: size, background: toneFor(name), fontSize: size * 0.38 }}
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white', className)}
    >
      {initials(name)}
    </span>
  );
}

export function AvatarStack({ people, max = 4 }: {
  people: { id: string; name: string; avatarUrl?: string | null }[]; max?: number;
}) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <div className="flex items-center -space-x-2 rtl:space-x-reverse">
      {shown.map((p) => (
        <Avatar key={p.id} name={p.name} src={p.avatarUrl} size={26}
                className="ring-2 ring-surface" />
      ))}
      {rest > 0 && (
        <span className="inline-flex size-[26px] items-center justify-center rounded-full bg-canvas-soft text-[10px] font-semibold text-ink-muted ring-2 ring-surface">
          +{rest}
        </span>
      )}
    </div>
  );
}
